import type { LevelAdat } from '../api.ts';
import { ALAP_DOKUMENTUM_CSS, esc, huDatum } from './dokumentum-ablak.ts';

/**
 * Fizetési felszólítás és egyenlegközlő. (Megbízás 2.0 / 5.6)
 *
 * Ugyanabból az adatból kétféle levél lesz. Ha van lejárt tétel, fizetést
 * kérünk; ha nincs, csak tájékoztatunk az egyenlegről. A szöveg is ehhez
 * igazodik — annak, aki még nem is késett, nem küldünk felszólítást.
 *
 * Minden adat a szerverről jön: a saját cégnév, székhely, adószám és a
 * bankszámlaszám a beállításokból. Semmi nincs ide égetve.
 */

function ft(n: number): string {
  return new Intl.NumberFormat('hu-HU').format(Math.round(n));
}

export const LEVEL_CSS = `
  .lap { font-size: 11pt; }
  .levfej { display: flex; justify-content: space-between; align-items: flex-start;
            border-bottom: 1.5px solid #000; padding-bottom: 10px; margin-bottom: 22px;
            font-size: 10.5pt; line-height: 1.5; }
  .levfej .jobb { text-align: right; }
  .cimzett { font-size: 11pt; line-height: 1.5; margin: 0 0 26px auto; width: 58%; }
  .levcim { font-size: 15pt; font-weight: 700; margin: 0 0 16px; letter-spacing: .02em; }
  .levp { font-size: 11pt; line-height: 1.6; margin: 0 0 12px; text-align: justify; }
  table.levt { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin: 14px 0 18px; }
  table.levt th { background: #F1F2EF; text-align: left; padding: 6px 8px; border: 1px solid #999;
                  font-size: 8.5pt; text-transform: uppercase; letter-spacing: .04em; color: #333; }
  table.levt td { padding: 5px 8px; border: 1px solid #999; }
  table.levt td.r, table.levt th.r { text-align: right;
                                     font-family: "Consolas", "Courier New", monospace; }
  table.levt td.k { color: #A8281F; font-weight: 700; }
  table.levt tr.ossz td { font-weight: 700; background: #F1F2EF; }
  .levalair { margin-top: 30px; font-size: 11pt; }
  .szamlaszam { white-space: nowrap; }
`;

/**
 * Egy levél lapja.
 *
 * A tételek határidő szerint állnak, tehát a legrégebbi tartozás van elöl
 * — az a fontos, azzal kell kezdeni.
 */
export function level(l: LevelAdat): string {
  const c = l.cimzett;
  const felszolitas = l.fajta === 'felszolitas';

  const bevezeto = felszolitas
    ? 'Nyilvántartásunk szerint az alábbi számláink fizetési határideje lejárt. ' +
      'Kérjük, szíveskedjenek a tartozást mielőbb rendezni.'
    : 'Az alábbiakban tájékoztatjuk Önöket a nyilvántartásunk szerinti nyitott számláinkról.';

  const sorok = c.tetelek
    .map(
      (t) => `<tr>
        <td>${esc(t.bizonylatszam)}</td>
        <td>${esc(t.telephely ?? '—')}</td>
        <td>${esc(t.kelt ?? '')}</td>
        <td>${esc(t.fizetesi_hatarido ?? '')}</td>
        <td class="r">${ft(t.brutto)}</td>
        <td class="r${t.keses > 0 ? ' k' : ''}">${t.keses > 0 ? `${t.keses} nap` : '—'}</td>
      </tr>`,
    )
    .join('');

  const lejartSor =
    felszolitas && l.lejart_osszesen !== l.osszesen
      ? `<p class="levp">Ebből <b>lejárt tartozás: ${ft(l.lejart_osszesen)} Ft</b>
         (${c.lejart_db} számla).</p>`
      : '';

  return `
<div class="lap">
  <div class="levfej">
    <div>
      <b>${esc(l.sajat.nev)}</b><br>
      ${esc(l.sajat.szekhely)}<br>
      Adószám: ${esc(l.sajat.adoszam)}
    </div>
    <div class="jobb">Kelt: ${huDatum(l.kelt)}</div>
  </div>

  <div class="cimzett">
    <b>${esc(c.ceg_nev)}</b><br>
    ${esc(c.szekhely ?? '—')}
    ${c.adoszam ? `<br>Adószám: ${esc(c.adoszam)}` : ''}
    ${c.email ? `<br>${esc(c.email)}` : ''}
  </div>

  <h2 class="levcim">${felszolitas ? 'Fizetési felszólítás' : 'Egyenlegközlő'}</h2>

  <p class="levp">Tisztelt Partnerünk!</p>
  <p class="levp">${bevezeto}</p>

  <table class="levt">
    <tr>
      <th>Bizonylat</th><th>Telephely</th><th>Kelt</th><th>Határidő</th>
      <th class="r">Összeg</th><th class="r">Késés</th>
    </tr>
    ${sorok}
    <tr class="ossz">
      <td colspan="4">Összesen</td>
      <td class="r">${ft(l.osszesen)} Ft</td>
      <td></td>
    </tr>
  </table>

  ${lejartSor}

  <p class="levp">Átutalás esetén kérjük, a közleményben tüntessék fel a bizonylatszámot.
    Bankszámlaszámunk: <b class="szamlaszam">${esc(l.sajat.bankszamla)}</b>.</p>
  <p class="levp">Amennyiben a fizetés időközben megtörtént, kérjük, tekintsék tárgytalannak.</p>

  <p class="levp levalair">Üdvözlettel:<br>${esc(l.sajat.nev)}</p>
</div>`;
}

/**
 * Egy levél önálló HTML-lapként. Ez megy át a szerverre, amikor
 * e-mailben küldjük — ugyanaz a szöveg és forma, mint nyomtatásban.
 */
export function levelLap(l: LevelAdat): string {
  const cim = l.fajta === 'felszolitas' ? 'Fizetési felszólítás' : 'Egyenlegközlő';
  return (
    '<!doctype html><html lang="hu"><head><meta charset="utf-8">' +
    `<title>${cim}</title><style>${ALAP_DOKUMENTUM_CSS}${LEVEL_CSS}</style></head>` +
    `<body><div class="lapok">${level(l)}</div></body></html>`
  );
}

/** Több cég levele egyetlen ablakba, egymás után. */
export function levelek(lista: LevelAdat[]): string {
  return lista.map(level).join('');
}
